import { createFileRoute } from "@tanstack/react-router"
import {
  CircleHelp,
  FileDown,
  FilePlus2,
  FileText,
  History,
  ListChecks,
  Lock,
  Sparkles,
  UserX,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { AnswerBox, Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../components/landing"
import { InvoiceGenerator } from "../components/landing/invoice-generator"
import { HeroBand, SectionTitle, StatRow } from "../components/landing/page-parts"

/** Copy source: docs/design/landing/copy-products.md (`/invoice-generator`) and the design page. */
export const ATO_TAX_INVOICES_URL =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices"
export const CHECKED = "27 Oct 2026"
export const REVIEWED = "29 October 2026"

export const invoiceGeneratorSeo = () =>
  buildHead({
    title: "Free invoice generator for Australian sole traders",
    description:
      "Make a PDF invoice or tax invoice with GST worked out for you. No account needed. It follows the ATO's list of tax invoice details.",
    path: "/invoice-generator",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Invoice generator" }])],
  })

export const Route = createFileRoute("/_brand-layout/invoice-generator")({
  head: () => {
    const { meta, links, scripts } = invoiceGeneratorSeo()
    return { meta, links, scripts }
  },
  component: InvoiceGeneratorPage,
})

const STATS = [
  { icon: FileDown, value: "PDF", label: "to download or print" },
  {
    icon: FileText,
    tone: "success",
    value: "Tax Invoice",
    label: "or a plain invoice if you are not registered for GST",
  },
  { icon: UserX, value: "No account", label: "needed" },
  { icon: Lock, tone: "success", value: "Nothing sent to us", label: "it runs in your browser" },
] as const

export const TAX_INVOICE_DETAILS = [
  "The words Tax Invoice",
  "Your name and ABN",
  "The date of the invoice",
  "A description of what you sold, with the quantity and price",
  "How much of each sale is taxable (subject to GST)",
  "The GST amount, or a statement that the total includes GST",
  "The buyer's name or ABN, if the sale is A$1,000 or more",
] as const

export const FAQ = [
  {
    question: "Is the generator really free?",
    answer: "Yes. You can make and download an invoice without an account.",
  },
  {
    question: "I am not registered for GST. What happens?",
    answer: "The tool leaves out GST and calls it an Invoice.",
  },
  {
    question: "Where are the numbers kept?",
    answer:
      "Nowhere. They stay on this page in your browser and are gone when you close or reload it. We do not receive them.",
  },
]

export function InvoiceGeneratorPage() {
  const source = (
    <SourceLine
      sources={[{ label: "ATO, tax invoices", href: ATO_TAX_INVOICES_URL }]}
      checked={CHECKED}
    />
  )
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Invoice generator" }]} />
      <HeroBand>
        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
          <span className="bg-success-soft text-success inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium">
            <Sparkles className="size-3.5" aria-hidden="true" />
            Free tool
          </span>
          <span className="inline-flex items-center gap-1.5">
            <History className="size-3.5" aria-hidden="true" />
            Last reviewed {REVIEWED}
          </span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          Free invoice generator for Australian sole traders
        </h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={source}>
            Fill in your details and the items, and the tool builds a PDF invoice. If you are
            registered for GST it makes a Tax Invoice with the GST shown; if not, a plain invoice.
            It follows the ATO&apos;s list of tax invoice details.
          </AnswerBox>
        </div>
        <StatRow label="What you get" stats={STATS} />
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="make-title">
        <SectionTitle id="make-title" icon={FilePlus2} tone="primary">
          Make an invoice
        </SectionTitle>
        <InvoiceGenerator />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="needs-title">
        <SectionTitle id="needs-title" icon={ListChecks} tone="success">
          What a tax invoice needs
        </SectionTitle>
        <ul aria-label="Tax invoice details" className="grid max-w-[70ch] list-disc gap-1.5 pl-5">
          {TAX_INVOICE_DETAILS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {source}
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="faq-title">
        <SectionTitle id="faq-title" icon={CircleHelp} tone="success">
          Common questions
        </SectionTitle>
        <FaqAccordion items={FAQ} />
      </section>

      <section
        aria-labelledby="app-title"
        className="bg-accent text-accent-foreground mx-band my-4 grid gap-1 rounded-lg px-5 py-4"
      >
        <h2 id="app-title" className="font-semibold">
          Want numbering, emailing and payment tracking?
        </h2>
        <p>Keep every invoice in one place with your trips and receipts. Free plan available.</p>
      </section>

      <CtaBand
        title="Send it, track it, get paid"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
